import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSdk } from '../../sdkContext';
import { fetchPieceDetails, fetchPieceEvents } from './api';
import { makeKey, formatStoreName } from './PieceDetailsPage.helpers';

export const usePieceDetails = (pieceId) => {
  const sdk = useSdk();

  const {
    data: pieceResponse,
    isLoading: isPieceLoading,
    isError: isPieceError,
  } = useQuery({
    queryKey: ['pieceDetails', pieceId],
    queryFn: () => fetchPieceDetails(sdk, pieceId),
    enabled: !!pieceId,
  });

  const {
    data: eventsResponse,
    isLoading: isEventsLoading,
    isError: isEventsError,
    isFetching: isEventsFetching,
  } = useQuery({
    queryKey: ['pieceEvents', pieceId],
    queryFn: () => fetchPieceEvents(sdk, pieceId),
    enabled: !!pieceId,
  });

  const pieceInfo = useMemo(() => {
    const pieceData = pieceResponse?.data;
    const includedArray = pieceResponse?.included || [];

    if (!pieceData) {
      return { pieceAttrs: null, productTitle: '-', currentStoreName: '-' };
    }

    const includedMap = includedArray.reduce((acc, item) => {
      const key = makeKey(item.type, item.id);
      if (key) acc[key] = item;
      return acc;
    }, {});

    const listingRef = pieceData.relationships?.listing?.data;
    const listingKey = listingRef ? makeKey(listingRef.type, listingRef.id) : null;
    const listing = listingKey ? includedMap[listingKey] : null;

    const providerRef = listing?.relationships?.provider?.data;
    const providerKey = providerRef ? makeKey(providerRef.type, providerRef.id) : null;
    const currentProvider = providerKey ? includedMap[providerKey] : null;

    return {
      pieceAttrs: pieceData.attributes || {},
      productTitle: listing?.attributes?.title || 'Brak danych',
      currentStoreName: formatStoreName(currentProvider?.attributes) || 'Brak danych',
    };
  }, [pieceResponse]);

  const sortedEvents = useMemo(() => {
    const responseBody = eventsResponse?.data?.data ? eventsResponse.data : eventsResponse;
    const slotsArray = responseBody?.data;
    const includedArray = responseBody?.included || [];

    if (!slotsArray || !Array.isArray(slotsArray)) return [];

    const includedMap = includedArray.reduce((acc, item) => {
      const key = makeKey(item.type, item.id);
      if (key) acc[key] = item;
      return acc;
    }, {});

    return [...slotsArray]
      .map((slot) => {
        const employeeData = slot.relationships?.employee?.data;
        const employeeKey = employeeData ? makeKey(employeeData.type, employeeData.id) : null;
        const employeeRef = employeeKey ? includedMap[employeeKey] : null;

        const fromListingData = slot.relationships?.transferFromListing?.data;
        const fromListingKey = fromListingData ? makeKey(fromListingData.type, fromListingData.id) : null;
        const fromListing = fromListingKey ? includedMap[fromListingKey] : null;

        const fromProviderData = fromListing?.relationships?.provider?.data;
        const fromProviderKey = fromProviderData ? makeKey(fromProviderData.type, fromProviderData.id) : null;
        const fromProvider = fromProviderKey ? includedMap[fromProviderKey] : null;

        const toListingData = slot.relationships?.transferToListing?.data;
        const toListingKey = toListingData ? makeKey(toListingData.type, toListingData.id) : null;
        const toListing = toListingKey ? includedMap[toListingKey] : null;

        const toProviderData = toListing?.relationships?.provider?.data;
        const toProviderKey = toProviderData ? makeKey(toProviderData.type, toProviderData.id) : null;
        const toProvider = toProviderKey ? includedMap[toProviderKey] : null;

        return {
          ...slot,
          employee: employeeRef?.attributes || null,
          transferFromProvider: fromProvider?.attributes || null,
          transferToProvider: toProvider?.attributes || null,
        };
      })
      .sort((a, b) => {
        const dateA = new Date(a.attributes?.createdAt || a.attributes?.start || 0).getTime();
        const dateB = new Date(b.attributes?.createdAt || b.attributes?.start || 0).getTime();
        return dateA - dateB;
      });
  }, [eventsResponse]);

  return {
    pieceAttrs: pieceInfo.pieceAttrs,
    productTitle: pieceInfo.productTitle,
    currentStoreName: pieceInfo.currentStoreName,
    sortedEvents,
    hasPieceData: !!pieceResponse,
    isLoading: isPieceLoading || isEventsLoading,
    isError: isPieceError || isEventsError,
    isEventsFetching,
  };
};